import {
  Button,
  DropdownListItem,
  Field,
  IconCheck20,
  IconKeyboardArrowDown20,
  Spinner,
} from '@lightspeed/unified-components-helios-theme/react'
import type { DropdownListRenderProps } from '@lightspeed/unified-react-components'
import { useT } from '@transifex/react'
import React, { useEffect, useRef, useState } from 'react'
import {
  OutletSelectDropdownList,
  OutletSelectProps,
} from './OutletSelectTypes'
import { OutletSearchInput } from './components/OutletSearchInput'
import { OutletSummary } from './components/OutletSummary'
import { useOutletSelect } from './hooks/useOutletSelect'

type OutletSelectRenderItemParams = Parameters<
  NonNullable<DropdownListRenderProps['renderItem']>
>[0]

export const OutletSelect: React.FC<OutletSelectProps> = props => {
  const {
    className = '',
    showLabel = false,
    portalTargetRef: providedPortalTargetRef,
  } = props
  const t = useT()
  const fallbackPortalTargetRef = useRef<HTMLDivElement | null>(null)
  const portalTargetRef = providedPortalTargetRef ?? fallbackPortalTargetRef
  const searchInputRef = useRef<HTMLInputElement | null>(null)
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const model = useOutletSelect(props)

  useEffect(() => {
    if (providedPortalTargetRef) {
      return
    }

    // Portal the dropdown to the body so it escapes the fulfilment popover's
    // clipping/scroll container while keeping Helios base styles available.
    const portalTarget = document.createElement('div')
    portalTarget.className = 'uc-base wr-outlet-select-dropdown-portal'
    document.body.appendChild(portalTarget)
    fallbackPortalTargetRef.current = portalTarget

    return () => {
      fallbackPortalTargetRef.current = null
      portalTarget.remove()
    }
  }, [providedPortalTargetRef])

  useEffect(() => {
    if (isDropdownOpen) {
      searchInputRef.current?.focus()
    }
  }, [isDropdownOpen])

  if (!model.shouldRender) {
    return null
  }

  const fieldHintSlot = model.shouldShowOutletHint && (
    <span className="text-neutral-soft">
      {t('Customer will receive items from this outlet', {
        _context: 'Fulfillment outlet selector hint',
      })}
    </span>
  )

  const dropdownHeaderSlot = (
    <OutletSearchInput
      searchInputRef={searchInputRef}
      searchTerm={model.searchTerm}
      onSearchTermChange={model.setSearchTerm}
    />
  )

  const dropdownTriggerSlot = (
    <Button
      type="button"
      aria-label={t('Select outlet', {
        _context: 'Button aria label for outlet picker.',
      })}
      disabled={model.isDisabled}
      appearance="secondary"
      size="medium"
      block
      customClasses={{
        container: 'gap-3 bg-none hover:bg-none active:bg-none',
        content: 'flex-1',
      }}
      suffixSlot={
        <span className="flex size-5 shrink-0 items-center justify-center text-neutral-soft">
          {model.isOutletOptionsLoading ? (
            <Spinner />
          ) : (
            <IconKeyboardArrowDown20 />
          )}
        </span>
      }
    >
      <OutletSummary
        outlet={model.triggerOutlet}
        showAvailability={!model.isDisabled}
      />
    </Button>
  )

  const dropdownFooterSlot = model.isOutletOptionsLoading ? (
    <div className="flex justify-center p-3">
      <Spinner />
    </div>
  ) : (
    model.filteredOutlets.length === 0 && (
      <div className="p-3 text-center typography-body-sm text-neutral-soft">
        {model.isEmpty ? model.emptyLabel : model.noResultsLabel}
      </div>
    )
  )

  return (
    <div className={`uc-base flex w-full flex-col font-general ${className}`}>
      <Field
        size="medium"
        customClasses={{
          root: 'w-full',
          label: 'typography-body-sm-emphasized',
          hint: 'typography-body-sm text-neutral-soft',
        }}
        labelSlot={
          showLabel
            ? t('Fulfillment outlet', {
                _context: 'Mark as unfulfilled modal',
              })
            : undefined
        }
        hintSlot={fieldHintSlot}
      >
        <OutletSelectDropdownList
          trapFocus={false}
          opened={isDropdownOpen}
          onOpened={setIsDropdownOpen}
          items={model.dropdownItems}
          value={model.selectedOutletId ? [model.selectedOutletId] : []}
          highlightedValue={model.highlightedOutletId}
          onHighlightChange={model.handleHighlightChange}
          portalTargetRef={portalTargetRef}
          positioning={{
            placement: 'bottom-start',
            gutter: 4,
            flip: false,
            sameWidth: true,
          }}
          onSelect={model.handleOutletSelect}
          renderItem={({ item, state }: OutletSelectRenderItemParams) => (
            <DropdownListItem
              key={item.value}
              value={item.value}
              highlighted={state.highlighted}
              selected={state.selected}
              disabled={state.disabled}
              checkmarkPosition="end"
              checkmarkSlot={state.selected ? <IconCheck20 /> : undefined}
            >
              {item.contentSlot ?? item.label}
            </DropdownListItem>
          )}
          customClasses={{
            positioner: 'wr-outlet-select-dropdown-positioner',
            content: 'max-h-[260px]',
          }}
          headerSlot={dropdownHeaderSlot}
          triggerSlot={dropdownTriggerSlot}
          footerSlot={dropdownFooterSlot}
        />
      </Field>
    </div>
  )
}
